import { produce, castDraft } from "immer";
import React from "react";
import * as widgets from "./widgets.js";
export const getInitialFormState = (form) => {
    const initialValuesAsEntries = form.iterator.map((fieldName) => {
        const field = form.fields[fieldName];
        const widget = field.widget;
        if (widget.subwidgets != null) {
            const subwidgetValue = Object.fromEntries(widget.subwidgets.map((subwidget) => {
                const formPrefix = form.prefix === "" ? "" : `${form.prefix}-`;
                const unprefixedName = subwidget.name.replace(`${formPrefix}${fieldName}_`, "");
                return [unprefixedName, subwidget.value];
            }));
            return [fieldName, subwidgetValue];
        }
        return [fieldName, widget.value];
    });
    return Object.fromEntries(initialValuesAsEntries);
};
export const getInitialFormSetState = (forms, initial) => {
    return forms.map((form, index) => initial?.[index] ?? getInitialFormState(form));
};
export const getInitialFormSetErrors = (forms) => {
    return forms.map((form, index) => form.errors);
};
export const getFormHandler = ({ form, values, initial, errors, setValues, setErrors, ...options }) => {
    const changeInterceptor = options.changeInterceptor ?? ((_, prevValues, nextValues) => nextValues);
    const fieldInterceptor = options.fieldInterceptor ?? ((fieldName, field) => field);
    const changeValues = (fieldName, getIncomingValues) => {
        setValues((prevValues) => {
            const incomingValues = getIncomingValues(prevValues);
            const nextValues = changeInterceptor(fieldName, prevValues, incomingValues);
            return nextValues;
        });
    };
    const reset = () => {
        setValues(() => initial);
        setErrors({});
    };
    const fields = Object.fromEntries(form.iterator.map((fieldName) => {
        const field = form.fields[fieldName];
        const error = errors?.[fieldName]?.[0] ?? null;
        const { help_text } = field;
        if (field.widget.subwidgets != null) {
            const subwidgets = field.widget.subwidgets.map((subwidget) => {
                const formPrefix = form.prefix === "" ? "" : `${form.prefix}-`;
                const unprefixedName = subwidget.name.replace(`${formPrefix}${fieldName}_`, "");
                const setSubwidgetValue = (value) => {
                    changeValues(fieldName, (prevValues) => {
                        const subwidgetValues = prevValues[fieldName];
                        const subwidgetValue = subwidgetValues[unprefixedName];
                        return {
                            ...prevValues,
                            [fieldName]: {
                                ...subwidgetValues,
                                [unprefixedName]: value,
                            },
                        };
                    });
                };
                const subfieldHandler = {
                    name: subwidget.name,
                    error,
                    help_text,
                    label: field.label,
                    disabled: false,
                    tag: subwidget.tag,
                    widget: subwidget,
                    value: values[fieldName][unprefixedName],
                    handler: setSubwidgetValue,
                };
                return subfieldHandler;
            });
            const subwidgetHandler = {
                name: field.widget.name,
                disabled: field.widget.attrs.disabled ?? false,
                label: field.label,
                error,
                help_text,
                tag: field.widget.tag,
                subwidgets,
            };
            return [
                fieldName,
                fieldInterceptor(fieldName, subwidgetHandler, values),
            ];
        }
        const fieldHandler = {
            name: field.widget.name,
            error,
            help_text,
            label: field.label,
            disabled: field.widget.attrs.disabled ?? false,
            tag: field.widget.tag,
            widget: field.widget,
            value: values[fieldName],
            handler: (value) => {
                changeValues(fieldName, (prevValues) => ({
                    ...prevValues,
                    [fieldName]: value,
                }));
            },
        };
        return [
            fieldName,
            fieldInterceptor(fieldName, fieldHandler, values),
        ];
    }));
    const iterate = (iterator, callback) => {
        return iterator.map((fieldName) => {
            return callback(fieldName, fields[fieldName]);
        });
    };
    const visibleFields = form.iterator
        .filter((fieldName) => fields[fieldName].tag !== "django.forms.widgets.HiddenInput")
        .map((fieldName) => fields[fieldName]);
    const hiddenFields = form.iterator
        .filter((fieldName) => fields[fieldName].tag === "django.forms.widgets.HiddenInput")
        .map((fieldName) => fields[fieldName]);
    const nonFieldErrors = errors?.["__all__"] ?? null;
    return {
        form,
        values,
        initial,
        errors,
        fields,
        nonFieldErrors,
        visibleFields,
        hiddenFields,
        iterate,
        reset,
        setErrors,
        setValues: (values) => {
            setValues(() => values);
        },
        setValue: (fieldName, value) => {
            changeValues(fieldName, (prevValues) => ({
                ...prevValues,
                [fieldName]: value,
            }));
        },
    };
};
export const useForm = (options) => {
    const form = {
        ...options.form,
        iterator: options.form.iterator.filter((field) => options.exclude == null || !options.exclude.includes(field)),
    };
    const initial = { ...getInitialFormState(form), ...options.initial };
    const [values, formSetValues] = React.useState(initial);
    const [errors, setErrors] = React.useState(form.errors);
    return getFormHandler({
        ...options,
        form,
        errors,
        setErrors,
        initial,
        values,
        setValues: (getValuesToSetFromPrevValues) => {
            formSetValues((prevValues) => getValuesToSetFromPrevValues(prevValues));
        },
    });
};
export const createIterator = () => (props) => {
    const defaultHandler = "form" in props.form
        ? props.form
        : useForm({
            form: props.form,
            changeInterceptor: props.changeInterceptor,
        });
    const handler = "form" in props.form ? props.form : defaultHandler;
    const fieldInterceptor = props.fieldInterceptor ?? ((fieldName, field, values) => field);
    const getIterator = () => {
        if (props.fields != null) {
            return props.fields;
        }
        if (props.exclude != null) {
            return handler.form.iterator.filter((field) => !props.exclude.includes(field));
        }
        return handler.form.iterator;
    };
    return (React.createElement(React.Fragment, null, handler.iterate(getIterator(), (fieldName, field) => (React.createElement(React.Fragment, { key: field.name }, props.children(fieldInterceptor(fieldName, field, handler.values)))))));
};
export const Widget = (props) => {
    const { field } = props;
    if ("subwidgets" in field) {
        return (React.createElement(React.Fragment, null, field.subwidgets.map((subwidget) => {
            return React.createElement(Widget, { key: subwidget.name, field: subwidget });
        })));
    }
    if (field.tag === "django.forms.widgets.HiddenInput") {
        return React.createElement("input", { type: "hidden", name: field.name, value: field.value ?? "" });
    }
    else if (field.tag === "django.forms.widgets.CheckboxInput") {
        return (React.createElement(widgets.CheckboxInput, { name: field.name, value: field.value, onChange: field.handler }));
    }
    else if (field.tag === "django.forms.widgets.Textarea") {
        return (React.createElement(widgets.Textarea, { name: field.name, value: field.value, onChange: field.handler }));
    }
    else if (field.tag === "django.forms.widgets.PasswordInput") {
        return (React.createElement(widgets.TextInput, { name: field.name, value: field.value, onChange: field.handler, type: "password" }));
    }
    else if (field.tag === "django.forms.widgets.TextInput" ||
        field.tag === "django.forms.widgets.DateInput" ||
        field.tag === "django.forms.widgets.URLInput" ||
        field.tag === "django.forms.widgets.EmailInput" ||
        field.tag === "django.forms.widgets.TimeInput" ||
        field.tag === "django.forms.widgets.NumberInput") {
        return (React.createElement(widgets.TextInput, { name: field.name, value: field.value, onChange: field.handler, placeholder: field.widget.attrs.placeholder }));
    }
    else if (field.tag === "django.forms.widgets.Select") {
        return (React.createElement(widgets.Select, { name: field.name, value: field.value, optgroups: field.widget.optgroups, onChange: field.handler }));
    }
    else if (field.tag === "django.forms.widgets.ClearableFileInput") {
        return React.createElement("input", { type: "file", name: field.name, value: field.value ?? "" });
    }
    else if (field.tag === "django.forms.widgets.SelectMultiple") {
        return (React.createElement("select", { name: field.name, multiple: true, value: field.value, onChange: (event) => {
                const value = Array.from(event.target.selectedOptions, (option) => option.value);
                field.handler(value);
            } }, field.widget.optgroups.map((optgroup) => {
            const value = (optgroup[1][0].value ?? "").toString();
            return (React.createElement("option", { key: value, value: value }, optgroup[1][0].label));
        })));
    }
    const exhastive = field;
    throw new Error(`Exhaustive {field.tag}`);
};
export const ManagementForm = ({ formSet, }) => {
    return (React.createElement(React.Fragment, null,
        React.createElement("input", { type: "hidden", name: `${formSet.prefix}-INITIAL_FORMS`, value: formSet.initial_form_count }),
        React.createElement("input", { type: "hidden", name: `${formSet.prefix}-TOTAL_FORMS`, value: formSet.total_form_count })));
};
export const useFormSet = (options) => {
    const createForm = (index) => {
        return produce(options.formSet.empty_form, (draftState) => {
            for (const fieldName of draftState.iterator) {
                const prefix = `${options.formSet.prefix}-${index}`;
                const field = draftState.fields[fieldName];
                const htmlName = `${prefix}-${field.name}`;
                draftState.fields[fieldName].widget.name = htmlName;
                draftState.fields[fieldName].widget.attrs.id = `id_${htmlName}`;
                draftState.prefix = prefix;
            }
        });
    };
    const formSetFromInitialValues = produce(options.formSet, (draftState) => {
        if (options.initial == null) {
            return;
        }
        draftState.total_form_count = options.initial.length;
        draftState.forms = options.initial.map((_, index) => {
            return castDraft(createForm(index));
        });
    });
    const [formSet, setFormSet] = React.useState(formSetFromInitialValues);
    const initialFormSetState = getInitialFormSetState(formSetFromInitialValues.forms, options.initial);
    const initialFormSetErrors = getInitialFormSetErrors(options.formSet.forms);
    const [values, formSetSetValues] = React.useState(initialFormSetState);
    const [errors, formSetSetErrors] = React.useState(initialFormSetErrors);
    const emptyFormValues = getInitialFormState(formSet.empty_form);
    const forms = formSet.forms.map((form, index) => {
        return getFormHandler({
            form,
            changeInterceptor: options.changeInterceptor,
            fieldInterceptor: options.fieldInterceptor,
            values: values[index] ?? emptyFormValues,
            errors: errors[index] ?? {},
            setErrors: (nextErrors) => {
                formSetSetErrors((prevErrors) => ({
                    ...prevErrors,
                    [index]: nextErrors,
                }));
            },
            initial: initialFormSetState[index] ?? emptyFormValues,
            setValues: (getValuesToSetFromPrevValues) => {
                formSetSetValues((prevValues) => {
                    const nextValues = getValuesToSetFromPrevValues(prevValues[index] ?? emptyFormValues);
                    return produce(prevValues, (draftState) => {
                        draftState[index] = castDraft(nextValues);
                    });
                });
            },
        });
    });
    const addForm = () => {
        const { total_form_count } = formSet;
        const extraForm = createForm(formSet.total_form_count);
        const updated = produce(formSet, (draftState) => {
            draftState.forms.push(castDraft(extraForm));
            draftState.total_form_count += 1;
        });
        setFormSet(updated);
        options.onAddForm?.(extraForm);
    };
    const clear = () => {
        setFormSet(produce(formSet, (draftState) => {
            formSetSetValues([]);
            draftState.forms = [];
            draftState.total_form_count = 0;
        }));
    };
    const setFormsAndValues = (values) => {
        formSetSetValues(values);
        const updated = produce(formSet, (draftState) => {
            draftState.total_form_count = values.length;
            draftState.forms = values.map((_, index) => {
                return castDraft(createForm(index));
            });
        });
        setFormSet(updated);
    };
    return {
        schema: formSet,
        values,
        forms,
        addForm,
        clear,
        setFormsAndValues,
        setErrors: formSetSetErrors,
        setValues: formSetSetValues,
    };
};
export const bindWidgetType = () => {
    const Iterator = createIterator();
    function createRenderer(callback) {
        const Renderer = (props) => {
            return React.createElement(Iterator, { ...props }, (field) => callback(field, props));
        };
        // This is not used, but here so that children is excluded from required props.
        Renderer.defaultProps = {
            children: () => null,
        };
        return Renderer;
    }
    return {
        createRenderer,
        Iterator,
    };
};
export const createCSRFToken = (Context) => {
    const CSRFToken = (props) => {
        const context = React.useContext(Context);
        return (React.createElement("input", { type: "hidden", name: "csrfmiddlewaretoken", value: context.csrf_token }));
    };
    return CSRFToken;
};
export const Form = (props) => {
    const form = "form" in props.form ? props.form : useForm({ form: props.form });
    if (props.as == "p") {
        return (React.createElement(React.Fragment, null,
            form.nonFieldErrors?.map((error, index) => (React.createElement("div", { key: index }, error))),
            form.hiddenFields.map((field, index) => (React.createElement(Widget, { key: index, field: field }))),
            form.visibleFields.map((field, index) => (React.createElement(React.Fragment, { key: field.name },
                field.error != null && React.createElement("div", null, field.error),
                React.createElement("p", null,
                    React.createElement("label", null, field.label),
                    field.help_text != null && React.createElement("span", null, field.help_text),
                    React.createElement(Widget, { field: field })))))));
    }
    else {
        return (React.createElement(React.Fragment, null,
            form.nonFieldErrors != null && (React.createElement("tr", null, form.nonFieldErrors.map((error, index) => (React.createElement("td", { key: index, colSpan: 2 }, error))))),
            React.createElement("tr", { style: { display: "none" } }, form.hiddenFields.map((field, index) => (React.createElement(Widget, { key: index, field: field })))),
            form.visibleFields.map((field, index) => (React.createElement("tr", { key: field.name },
                React.createElement("th", null,
                    React.createElement("label", null, field.label)),
                React.createElement("td", null,
                    field.error != null && React.createElement("div", null, field.error),
                    field.help_text != null && (React.createElement(React.Fragment, null,
                        React.createElement("br", null),
                        React.createElement("span", null, field.help_text))),
                    React.createElement(Widget, { field: field })))))));
    }
};
export const FormSet = (props) => {
    return (React.createElement(React.Fragment, null,
        React.createElement(ManagementForm, { formSet: props.formSet }),
        props.formSet.forms.map((form) => (React.createElement(Form, { key: form.prefix, form: form, as: props.as })))));
};
// Used by Joy, but unsure what this is for.
export const getValue = (optgroup) => {
    const rawValue = optgroup[1][0].value;
    if (rawValue == null) {
        return "";
    }
    else if (rawValue === true) {
        return "True";
    }
    else if (rawValue === false) {
        return "False";
    }
    return rawValue;
};
//# sourceMappingURL=index.js.map